import type { InformationForUseClause } from '@/shared/hooks/api/standards'
import type { ExtractPreviewResponse } from '@/shared/hooks/api/userStandards'
import { useEntityDisplayLocales } from '../../hooks/useEntityDisplayLocales'
import {
  type EntityDisplayLocale,
  resolveInformationForUseAvailableLocales,
  resolveLocalizedInformationForUseField,
} from '@/shared/lib/standardEntityLocalizations'
import { StandardEntityLocaleToggle } from '../StandardEntityLocaleToggle'
import {
  ExtractionPreviewImpedimentBanner,
  ExtractionPreviewNoData,
  ExtractionPreviewSectionNotFound,
  type SectionExtractionPreviewContentProps,
} from './standardExtractionPreviewShared'

function resolveInformationForUseClauseDisplay(
  clause: InformationForUseClause,
  locale: EntityDisplayLocale,
  useSourceFields: boolean
): string {
  if (useSourceFields) {
    return clause.fullStatement || clause.content || clause.title || '—'
  }
  return (
    resolveLocalizedInformationForUseField(clause, locale, 'fullStatement') ||
    clause.content ||
    resolveLocalizedInformationForUseField(clause, locale, 'title') ||
    '—'
  )
}

type StandardInformationForUseExtractionPreviewContentProps =
  SectionExtractionPreviewContentProps & {
    preview: ExtractPreviewResponse
  }

export function StandardInformationForUseExtractionPreviewContent({
  preview,
  contentVariant = 'display',
  uiState,
  resolveReason,
  translate,
}: StandardInformationForUseExtractionPreviewContentProps) {
  const { getDisplayLocale, selectItemLocale } = useEntityDisplayLocales()
  const useSourceFields = contentVariant === 'source'
  const clauses = (preview.informationForUse?.clauses ??
    []) as InformationForUseClause[]
  const ifuImpediment = preview.informationForUseImpediment ?? null

  if (uiState.sectionNotFound) {
    return (
      <ExtractionPreviewSectionNotFound
        reasonCode={ifuImpediment?.reason ?? 'ifu_not_found'}
        resolveReason={resolveReason}
        translate={translate}
      />
    )
  }
  if (clauses.length > 0) {
    return (
      <ul className='space-y-0 divide-y rounded-md border px-4 py-2'>
        {clauses.map((clause) => {
          const availableLocales =
            resolveInformationForUseAvailableLocales(clause)
          const itemLocale = getDisplayLocale(clause.id)
          return (
            <li
              key={clause.id}
              className='flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0 first:last:py-0'
            >
              <div className='min-w-0 flex-1'>
                {clause.number != null && clause.number !== '' && (
                  <span className='font-medium'>{clause.number}. </span>
                )}
                {resolveInformationForUseClauseDisplay(
                  clause,
                  itemLocale,
                  useSourceFields
                )}
              </div>
              {!useSourceFields && (
                <StandardEntityLocaleToggle
                  availableLocales={availableLocales}
                  selectedLocale={itemLocale}
                  onSelectLocale={(locale) => selectItemLocale(clause.id, locale)}
                />
              )}
            </li>
          )
        })}
      </ul>
    )
  }
  if (ifuImpediment != null) {
    return (
      <ExtractionPreviewImpedimentBanner
        label={translate('detail.extractPreview.noInformationForUseExtracted')}
        reason={resolveReason(ifuImpediment.reason)}
      />
    )
  }
  return <ExtractionPreviewNoData translate={translate} />
}
